import React, { useMemo } from 'react';
import classNames from 'classnames';
import MaskedInput from 'react-text-mask';
import { Input, Select } from 'components/ui';
import { phoneNumberMask } from 'utils/formMasks';
import { phoneCountries, PRIORITY_DDIS } from 'constants/phoneCountries';
import { DEFAULT_DDI, isBrazilDdi, normalizeDdi, onlyDigits } from 'utils/phone';

const E164_MAX_DIGITS = 15;

/** Prioriza os países mais comuns no topo, preservando a ordem alfabética do restante. */
const buildOptions = () => {
  const priority = PRIORITY_DDIS.map((ddi) =>
    phoneCountries.find((c) => c.ddi === ddi)
  ).filter(Boolean);
  const prioritySet = new Set(priority.map((c) => c.iso));
  const rest = phoneCountries.filter((c) => !prioritySet.has(c.iso));

  return [...priority, ...rest].map((country) => ({
    value: country.ddi,
    iso: country.iso,
    flag: country.flag,
    country: country.name,
    // O label é o que o react-select usa para busca: permite achar por nome ou por DDI.
    label: `${country.name} +${country.ddi}`,
  }));
};

/**
 * Telefone com seletor de DDI. O número é sempre o nacional (sem DDI); quem monta o valor
 * final para o banco é `buildStoredPhone` em utils/phone.
 *
 * Brasil mantém a máscara "(DD) NNNNN-NNNN" já usada no produto; para outros países o campo
 * aceita dígitos livres, porque cada país tem formato próprio.
 */
const PhoneInput = ({
  value = '',
  ddi = DEFAULT_DDI,
  onChange,
  onDdiChange,
  onBlur,
  name = 'telephone',
  placeholder,
  disabled = false,
  invalid = false,
  autoComplete = 'off',
  className,
}) => {
  const options = useMemo(buildOptions, []);
  const currentDdi = normalizeDdi(ddi);
  const selected = options.find((o) => o.value === currentDdi) || options[0];
  const brazilian = isBrazilDdi(currentDdi);

  const handleNumberChange = (event) => {
    const raw = event.target.value;
    onChange(brazilian ? raw : onlyDigits(raw).slice(0, E164_MAX_DIGITS - currentDdi.length));
  };

  return (
    <div className={classNames('flex items-start gap-2', className)}>
      <Select
        className="w-40 shrink-0"
        instanceId={`${name}-ddi`}
        options={options}
        value={selected}
        isDisabled={disabled}
        onChange={(option) => {
          const nextDdi = option?.value || DEFAULT_DDI;
          if (nextDdi === currentDdi) return;
          onDdiChange(nextDdi);
          // Formatos são incompatíveis entre países: limpa para evitar número híbrido.
          onChange('');
        }}
        placeholder="País"
        formatOptionLabel={(option, meta) =>
          meta.context === 'menu' ? (
            <span className="flex items-center gap-2">
              <span aria-hidden="true">{option.flag}</span>
              <span className="truncate">{option.country}</span>
              <span className="ml-auto text-gray-500">+{option.value}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span aria-hidden="true">{option.flag}</span>
              <span>+{option.value}</span>
            </span>
          )
        }
      />
      {brazilian ? (
        <Input
          className="flex-1"
          name={name}
          value={value}
          onChange={handleNumberChange}
          onBlur={onBlur}
          disabled={disabled}
          invalid={invalid}
          autoComplete={autoComplete}
          placeholder={placeholder || '(11) 91234-5678'}
          asElement={MaskedInput}
          mask={phoneNumberMask}
          inputMode="tel"
        />
      ) : (
        <Input
          className="flex-1"
          name={name}
          value={value}
          onChange={handleNumberChange}
          onBlur={onBlur}
          disabled={disabled}
          invalid={invalid}
          autoComplete={autoComplete}
          placeholder={placeholder || 'Número com código de área'}
          inputMode="tel"
          maxLength={E164_MAX_DIGITS - currentDdi.length}
        />
      )}
    </div>
  );
};

export default PhoneInput;
